import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEditorState, type Editor } from '@tiptap/react'
import {
  Bold,
  ChevronsUpDown,
  Highlighter,
  Italic,
  RotateCcw,
  SmilePlus,
  Strikethrough,
  Underline,
  type LucideIcon,
} from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/src/renderer/components/ui/popover'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { IconPicker, type IconPickerValue } from '@/src/renderer/components/common/IconPicker'
import { ColorDrop } from '@/src/renderer/components/icons/ColorDrop'
import { CanvasFontMenu } from '../ProjectCanvas/optionsBar/CanvasFontMenu'
import { CanvasTextSizeMenu } from '../ProjectCanvas/optionsBar/CanvasTextSizeMenu'
import { CanvasColorPicker } from '../ProjectCanvas/optionsBar/CanvasColorPicker'
import { Caret } from '../ProjectCanvas/optionsBar/CanvasOptionsBar'
import '../ProjectCanvas/optionsBar/CanvasOptionsBar.scss'
import { getCanvasNoteFont } from '../ProjectCanvas/utils/canvasFonts'
import { getCanvasTextHighlight } from '../ProjectCanvas/utils/canvasText'
import { randomCanvasTextMarkerLook } from '../ProjectCanvas/utils/canvasTextMarker'
import { TITLE_FOREGROUND, TITLE_WHITE, parseTitleColorKey } from '@/src/renderer/lib/projectIcon/titlePalette'
import { TitleColorPicker } from './TitleColorPicker'
import {
  createTitlePreview,
  resetTitleFormat,
  setTitleHighlight,
  setTitleStyle,
  shuffleTitleMarkers,
  toggleTitleMark,
  type TitleMark,
} from './titleFormat'

/** Die Größe eines Titels, an dem nichts eingestellt ist (`ProjectPage.scss`). */
export const TITLE_DEFAULT_SIZE = 18

/**
 * Die Stufen des Titels (gewünscht 2026-09-26): nichts unter dem Standard,
 * nach oben weit gespreizt — 22 und 26 wirkten nicht groß.
 */
const TITLE_SIZES = [
  { key: 'default', size: TITLE_DEFAULT_SIZE },
  { key: 'large', size: 24 },
  { key: 'extraLarge', size: 32 },
] as const

/** Die Pfeile zählen um 2 statt 8 — die Stufen liegen dicht beieinander. */
const TITLE_SIZE_STEP = 2

const MARKS: readonly {
  mark: TitleMark
  icon: LucideIcon
  shortcut: { kind: 'character'; text: string; mod: true; shift?: true }
}[] = [
  { mark: 'bold', icon: Bold, shortcut: { kind: 'character', text: 'B', mod: true } },
  { mark: 'italic', icon: Italic, shortcut: { kind: 'character', text: 'I', mod: true } },
  { mark: 'underline', icon: Underline, shortcut: { kind: 'character', text: 'U', mod: true } },
  { mark: 'strike', icon: Strikethrough, shortcut: { kind: 'character', text: 'S', mod: true, shift: true } },
]

interface ToolbarState {
  bold: boolean
  italic: boolean
  underline: boolean
  strike: boolean
  color: string | null
  font: string | null
  size: number | null
  highlight: string | null
  hasMarkers: boolean
}

type Flyout = 'font' | 'size' | 'color' | 'highlight' | null

export interface TitleToolbarProps {
  /** Der Editor des Titels — `null`, solange er noch nicht steht. */
  editor: Editor | null
  /** Das Projekt-Icon (`Project.icon`). */
  icon: string | null | undefined
  /** Eine Farbe unter dem Zeiger im Icon-Wähler — Vorschau fürs Icon über dem Titel; `undefined` = vorbei (#246). */
  onIconPreview?: (preview: IconPickerValue | undefined) => void
  onIconChange: (next: IconPickerValue | null) => void
  /** Eine Wahl ist fertig (z. B. eine Stufe im Fächer) — das ganze Werkzeug geht zu. */
  onDone?: () => void
  /** Eine Vorschau beginnt (`true`) oder endet — der Titel zeigt gerade einen nur überfahrenen Wert. */
  onPreviewChange?: (active: boolean) => void
  /**
   * Nach jeder Formatierung aus dem Werkzeug: der Titel als HTML und
   * Klartext — er wird sofort gespeichert, nicht erst beim Verlassen des
   * Schreibfelds (gemeldet 2026-09-26: die Sidebar zeigte die Farbe zu spät).
   */
  onCommit?: (html: string, plain: string) => void
}

/** Klicks in der Leiste lassen den Fokus (und die Markierung) im Titel. */
const keepFocus = (e: React.MouseEvent) => e.preventDefault()

/**
 * Das **Titelwerkzeug** (CONTEXT.md „Projekt-Kopf“): Icon, Schrift, Größe,
 * Farbe, Fett/Kursiv/Unterstrichen/Durchgestrichen, Hervorheben und
 * Zurücksetzen. **Gedeckt im Ton des Lebensbereichs**, so kräftig wie die Leiste der Pinnwand;
 * gebaut aus den Teilen der Options-Leiste der Pinnwand.
 *
 * Jede Einstellung gilt der Markierung im Titel, ohne Markierung dem ganzen
 * Titel. Es formatiert über den Editor, den es bekommt; der Fokus bleibt dabei
 * im Titel.
 *
 * @example
 * <TitleToolbar editor={titleEditor} icon={project.icon} onIconChange={saveIcon} />
 */
export const TitleToolbar: React.FC<TitleToolbarProps> = ({ editor, icon, onIconChange, onIconPreview, onDone, onCommit, onPreviewChange }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState<Flyout>(null)
  const [iconOpen, setIconOpen] = useState(false)
  const toggle = (flyout: Flyout) => setOpen((current) => (current === flyout ? null : flyout))

  // Während einer Vorschau zeigt das Werkzeug weiter den eingestellten Stand:
  // Sonst hielte der Marker-Wähler die überfahrene dunkle Stufe für gewählt,
  // und beide Tropfen tauschten schon beim Überfahren die Plätze (gemeldet
  // 2026-09-26). Erst der Klick ändert, was gewählt ist.
  const previewRef = useRef<ReturnType<typeof createTitlePreview> | null>(null)
  // Eine Form je Marker-Wahl — Vorschau und Klick tragen dieselbe, wie auf der
  // Pinnwand (`TextNode`). Erst nach der Wahl gibt es eine neue.
  const markerLookRef = useRef<number | null>(null)
  const markerLook = () => (markerLookRef.current ??= randomCanvasTextMarkerLook())
  const settledRef = useRef<ToolbarState | null>(null)
  const state = useEditorState({
    editor,
    selector: ({ editor: e }): ToolbarState | null => {
      if (!e) return null
      if (previewRef.current?.active && settledRef.current) return settledRef.current
      const style = e.getAttributes('canvasTextStyle') as {
        color?: string | null
        font?: string | null
        size?: number | null
      }
      const highlight = e.isActive('canvasHighlight') ? (e.getAttributes('canvasHighlight').color as string) : null
      let hasMarkers = false
      e.state.doc.descendants((node) => {
        if (node.marks.some((m) => m.type.name === 'canvasHighlight')) hasMarkers = true
        return !hasMarkers
      })
      settledRef.current = {
        bold: e.isActive('bold'),
        italic: e.isActive('italic'),
        underline: e.isActive('underline'),
        strike: e.isActive('strike'),
        color: style.color ?? null,
        font: style.font ?? null,
        size: style.size ?? null,
        highlight,
        hasMarkers,
      }
      return settledRef.current
    },
  })

  // Die Schrift zeigt sich beim Überfahren schon im Titel (wie auf der Pinnwand).
  const onPreviewChangeRef = useRef(onPreviewChange)
  useEffect(() => { onPreviewChangeRef.current = onPreviewChange }, [onPreviewChange])
  const preview = useMemo(
    () => (editor ? createTitlePreview(editor, active => onPreviewChangeRef.current?.(active)) : null),
    [editor],
  )
  useEffect(() => { previewRef.current = preview }, [preview])
  useEffect(() => {
    if (open !== 'font' && open !== 'color' && open !== 'highlight') preview?.restore()
  }, [open, preview])
  useEffect(() => () => preview?.restore(), [preview])

  if (!editor || !state) return null

  /** Formatieren und gleich speichern. */
  const commit = <A extends unknown[]>(format: (e: Editor, ...args: A) => void, target: Editor, ...args: A) => {
    format(target, ...args)
    onCommit?.(target.getHTML(), target.getText())
  }

  const font = getCanvasNoteFont(state.font)
  const size = state.size ?? TITLE_DEFAULT_SIZE
  const preset = TITLE_SIZES.find((entry) => entry.size === size)?.key
  const sizeLabel = preset ? t(`projectCanvas.text.size.${preset}`) : String(size)
  const color = parseTitleColorKey(state.color)

  return (
    <div className="canvas-options-bar title-toolbar" role="toolbar" aria-label={t('projectTitle.toolbar')}>
      <div className="canvas-options-bar__item">
        <Popover open={iconOpen} onOpenChange={(next) => { setIconOpen(next); if (!next) onIconPreview?.(undefined) }}>
          <WithTooltip content={t('projectTitle.icon')}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="canvas-options-bar__button canvas-options-bar__button--font"
                aria-label={t('projectTitle.icon')}
                onMouseDown={keepFocus}
              >
                <SmilePlus size={16} strokeWidth={2} />
                <ChevronsUpDown size={16} strokeWidth={2} />
              </button>
            </PopoverTrigger>
          </WithTooltip>
          <PopoverContent align="start" sideOffset={8} onOpenAutoFocus={(e) => e.preventDefault()}>
            <IconPicker
              value={icon}
              onValueChange={(next) => {
                onIconChange(next)
                // Eine Wahl lässt den Wähler offen (#246) — er geht beim Klick daneben zu; Entfernen schließt.
                if (next === null) {
                  setIconOpen(false)
                  onIconPreview?.(undefined)
                }
              }}
              onPreview={onIconPreview}
            />
          </PopoverContent>
        </Popover>
      </div>

      <span className="canvas-options-bar__divider" aria-hidden="true" />

      <div className="canvas-options-bar__item title-toolbar__item--from-bar">
        <WithTooltip content={t('projectTitle.color')}>
          <button
            type="button"
            className="canvas-options-bar__button canvas-options-bar__button--wide"
            aria-label={t('projectTitle.color')}
            aria-haspopup="listbox"
            aria-expanded={open === 'color'}
            onMouseDown={keepFocus}
            onClick={() => toggle('color')}
          >
            {/* Wie der Farb-Knopf eines Pinnwand-Elements: Tropfen mit Rand, darunter der kleine Winkel. */}
            <span className="title-color-picker__ink" data-color={state.color ?? 'default'}>
              <ColorDrop fill="currentColor" size={16} className="canvas-options-bar__chip" />
            </span>
            <Caret />
          </button>
        </WithTooltip>
        {open === 'color' && (
          <TitleColorPicker
            value={color || state.color === TITLE_FOREGROUND || state.color === TITLE_WHITE ? state.color : null}
            onPreview={(key) => (key === undefined ? preview?.restore() : preview?.show('color', key))}
            onValueChange={(key, done) => {
              preview?.restore()
              commit(setTitleStyle, editor, 'color', key)
              // Eine fertige Wahl (Stufe, Schwarz, keine Farbe) schließt alle Leisten (gewünscht 2026-09-26).
              if (done) {
                setOpen(null)
                onDone?.()
              }
            }}
          />
        )}
      </div>

      <span className="canvas-options-bar__divider" aria-hidden="true" />

      <div className="canvas-options-bar__item">
        <WithTooltip content={t('projectTitle.font')}>
          <button
            type="button"
            className="canvas-options-bar__button canvas-options-bar__button--font"
            aria-label={t('projectTitle.font')}
            aria-haspopup="listbox"
            aria-expanded={open === 'font'}
            onMouseDown={keepFocus}
            onClick={() => toggle('font')}
          >
            <span
              className="canvas-options-bar__font-label title-toolbar__font-label"
              data-font={state.font ?? undefined}
            >
              {font.label}
            </span>
            <ChevronsUpDown size={16} strokeWidth={2} />
          </button>
        </WithTooltip>
        {open === 'font' && (
          <CanvasFontMenu
            value={state.font}
            placement="below"
            onPreview={(choice) => (choice ? preview?.show('font', choice.font) : preview?.restore())}
            onPick={(key) => {
              preview?.restore()
              commit(setTitleStyle, editor, 'font', key)
              setOpen(null)
            }}
          />
        )}
      </div>

      <div className="canvas-options-bar__item">
        <WithTooltip content={t('projectTitle.size')}>
          <button
            type="button"
            className="canvas-options-bar__button canvas-options-bar__button--font"
            aria-label={t('projectTitle.size')}
            aria-haspopup="listbox"
            aria-expanded={open === 'size'}
            onMouseDown={keepFocus}
            onClick={() => toggle('size')}
          >
            <span className="canvas-options-bar__font-label">{sizeLabel}</span>
            <ChevronsUpDown size={16} strokeWidth={2} />
          </button>
        </WithTooltip>
        {open === 'size' && (
          <CanvasTextSizeMenu
            value={size}
            presets={TITLE_SIZES}
            step={TITLE_SIZE_STEP}
            autoFocus
            placement="below"
            onPick={(value) => {
              commit(setTitleStyle, editor, 'size', value === TITLE_DEFAULT_SIZE ? null : value)
              setOpen(null)
            }}
            onStep={(value) => commit(setTitleStyle, editor, 'size', value)}
          />
        )}
      </div>

      <span className="canvas-options-bar__divider" aria-hidden="true" />

      <div className="canvas-options-bar__group">
        {MARKS.map(({ mark, icon: Icon, shortcut }) => (
          <WithTooltip key={mark} content={t(`projectTitle.${mark}`)} shortcut={shortcut}>
            <button
              type="button"
              className={`canvas-options-bar__button${state[mark] ? ' is-active' : ''}`}
              aria-label={t(`projectTitle.${mark}`)}
              aria-pressed={state[mark]}
              onMouseDown={keepFocus}
              onClick={() => commit(toggleTitleMark, editor, mark)}
            >
              <Icon size={16} strokeWidth={2} />
            </button>
          </WithTooltip>
        ))}
      </div>

      <div className="canvas-options-bar__item">
        <WithTooltip content={t('projectTitle.highlight')}>
          <button
            type="button"
            className="canvas-options-bar__button canvas-options-bar__button--wide"
            aria-label={t('projectTitle.highlight')}
            aria-haspopup="listbox"
            aria-expanded={open === 'highlight'}
            onMouseDown={keepFocus}
            onClick={() => toggle('highlight')}
          >
            <span
              className={`canvas-options-bar__highlight${state.highlight ? ' canvas-options-bar__highlight--on' : ''}`}
              style={
                state.highlight
                  ? ({ '--highlight': getCanvasTextHighlight(state.highlight) } as React.CSSProperties)
                  : undefined
              }
            >
              <Highlighter size={16} strokeWidth={2} />
            </span>
          </button>
        </WithTooltip>
        {open === 'highlight' && (
          <CanvasColorPicker
            kind="highlight"
            value={state.highlight}
            defaultSelected={state.highlight === null}
            placement="below"
            onPreview={(choice) => (choice ? preview?.show('highlight', choice.color, markerLook()) : preview?.restore())}
            onPick={(key) => {
              preview?.restore()
              commit(setTitleHighlight, editor, key, markerLook())
              markerLookRef.current = null
              setOpen(null)
            }}
            onPickDefault={() => {
              preview?.restore()
              commit(setTitleHighlight, editor, null)
              setOpen(null)
            }}
            onShuffle={() => {
              preview?.restore()
              commit(shuffleTitleMarkers, editor)
              markerLookRef.current = null
            }}
            shuffleDisabled={!state.hasMarkers}
          />
        )}
      </div>

      <span className="canvas-options-bar__divider" aria-hidden="true" />

      <WithTooltip content={t('projectTitle.reset')}>
        <button
          type="button"
          className="canvas-options-bar__button"
          aria-label={t('projectTitle.reset')}
          onMouseDown={keepFocus}
          onClick={() => {
            commit(resetTitleFormat, editor)
            setOpen(null)
          }}
        >
          <RotateCcw size={16} strokeWidth={2} />
        </button>
      </WithTooltip>
    </div>
  )
}
